import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bookmark, Check, ChevronDown, FileDown, Filter, Loader2, Trash2, X } from 'lucide-react';
import { statusLabel } from '../EstimateStatusSelect';

export const FILTER_DIMS = [['status', 'Lifecycle'], ['stage', 'Deal stage'], ['region', 'Region'], ['rep', 'Rep'], ['complexity', 'Complexity'], ['deal_type', 'Deal type'], ['fy', 'Financial year']];
export const EMPTY_FILTERS = { status: [], stage: [], region: [], rep: [], complexity: [], deal_type: [], fy: [] };
export const normalizeFilters = (f) => Object.fromEntries(FILTER_DIMS.map(([k]) => [k, Array.isArray(f?.[k]) ? f[k] : f?.[k] ? [f[k]] : []]));
export const filterParams = (f) => Object.fromEntries(FILTER_DIMS.filter(([k]) => f[k]?.length).map(([k]) => [k, f[k].join(',')]));
export const activeFilterCount = (f) => FILTER_DIMS.reduce((n, [k]) => n + (f[k]?.length ? 1 : 0), 0);
const API_URL = process.env.REACT_APP_BACKEND_URL;
const sameFilters = (a, b) => FILTER_DIMS.every(([k]) => JSON.stringify([...(a?.[k] || [])].sort()) === JSON.stringify([...(b?.[k] || [])].sort()));

/** Auto-name like "ANZ · Won · Complex" from the selected values (labels from facets). */
export const autoViewName = (filters, facets) => FILTER_DIMS.flatMap(([k]) => (filters[k] || []).map((v) => {
  const o = (facets?.[k] || []).find((x) => x.value === v);
  return k === 'status' ? statusLabel(v) : o?.label || v;
})).join(' · ') || 'All deals';

function SavedViews({ views, setViews, filters, setFilters, facets }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const current = views.find((v) => sameFilters(v.filters, filters));
  const save = () => {
    const name = window.prompt('Name this view', autoViewName(filters, facets));
    if (name == null || !name.trim()) return;
    const n = name.trim().slice(0, 60);
    setViews([...views.filter((v) => v.name.toLowerCase() !== n.toLowerCase()), { name: n, filters: normalizeFilters(filters) }]);
    toast.success(`View "${n}" saved`); setOpen(false);
  };
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="margin-views-btn"
        className={`h-7 inline-flex items-center gap-1 rounded-md border px-2 text-[11px] ${current ? 'border-[#0F2040] bg-[#0F2040] text-white' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}>
        <Bookmark className="w-3 h-3" /> {current ? current.name : views.length ? `Views (${views.length})` : 'Views'} <ChevronDown className="w-3 h-3 opacity-70" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 min-w-[240px] rounded-md border border-slate-200 bg-white shadow-lg p-1" data-testid="margin-views-menu">
          {!views.length && <p className="px-2 py-1.5 text-[11px] text-slate-400">No saved views yet — set some filters and save them.</p>}
          {views.map((v) => (
            <div key={v.name} className={`flex items-center gap-1 rounded px-1 ${sameFilters(v.filters, filters) ? 'bg-cyan-50' : 'hover:bg-slate-50'}`}>
              <button type="button" onClick={() => { setFilters(normalizeFilters(v.filters)); setOpen(false); }} className="flex-1 text-left px-1 py-1 text-[11px] text-slate-700" title={autoViewName(normalizeFilters(v.filters), facets)} data-testid={`margin-view-apply-${v.name}`}>{v.name}</button>
              <button type="button" onClick={() => { setViews(views.filter((x) => x.name !== v.name)); toast.info(`View "${v.name}" removed`); }} className="text-slate-300 hover:text-rose-600" title="Delete view" data-testid={`margin-view-delete-${v.name}`}><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
          <button type="button" onClick={save} disabled={!activeFilterCount(filters)} className="w-full mt-1 flex items-center gap-1 rounded border-t border-slate-100 px-2 py-1.5 text-left text-[11px] text-[#0F2040] font-medium hover:bg-slate-50 disabled:opacity-40" data-testid="margin-view-save">
            <Bookmark className="w-3 h-3" /> Save current filters as a view…
          </button>
        </div>
      )}
    </div>
  );
}

function Dim({ dimKey, label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  const lbl = (o) => (dimKey === 'status' ? statusLabel(o.value) : o.label);
  const summary = !selected.length ? 'all' : selected.length === 1 ? lbl(options.find((o) => o.value === selected[0]) || { value: selected[0], label: selected[0] }) : `${selected.length} selected`;
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`margin-filter-${dimKey}`}
        className={`h-7 inline-flex items-center gap-1 rounded-md border px-2 text-[11px] ${selected.length ? 'border-[#4ABFCF] bg-cyan-50 text-[#0F2040] font-medium' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}>
        {label}: {summary} <ChevronDown className="w-3 h-3 text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 min-w-[220px] max-h-72 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg p-1" data-testid={`margin-filter-${dimKey}-menu`}>
          {!options.length && <p className="px-2 py-1.5 text-[11px] text-slate-400">No values</p>}
          {options.map((o) => (
            <button key={o.value} type="button" onClick={() => toggle(o.value)} data-testid={`margin-filter-${dimKey}-opt-${o.value}`}
              className="w-full flex items-center gap-2 rounded px-2 py-1 text-left text-[11px] text-slate-700 hover:bg-slate-50">
              <span className={`inline-flex w-3.5 h-3.5 items-center justify-center rounded border ${selected.includes(o.value) ? 'bg-[#0F2040] border-[#0F2040] text-white' : 'border-slate-300'}`}>{selected.includes(o.value) && <Check className="w-2.5 h-2.5" />}</span>
              <span className="flex-1 truncate">{lbl(o)}</span>
              <span className="text-[10px] text-slate-400 tabular-nums">{o.count}</span>
            </button>
          ))}
          {selected.length > 0 && <button type="button" onClick={() => onChange([])} className="w-full mt-1 text-[10px] text-slate-500 underline px-2 py-1 text-left" data-testid={`margin-filter-${dimKey}-clear`}>clear {label.toLowerCase()}</button>}
        </div>
      )}
    </div>
  );
}

/** Page-level deal filters for the Margin tab — every card recomputes server-side on the filtered set. */
export function MarginFilterBar({ facets, filters, setFilters, matched, universe, views = [], setViews }) {
  const active = activeFilterCount(filters);
  const [packing, setPacking] = useState(false);
  const downloadPack = async () => {
    setPacking(true);
    try {
      const r = await axios.get(`${API_URL}/api/insights/margin/deal-pack.pdf`, { params: filterParams(filters), withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = `Deal_Review_Pack_${active ? autoViewName(filters, facets).replace(/[^\w]+/g, '_') + '_' : ''}${new Date().toISOString().slice(0, 10)}.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success(active ? `Deal review pack built for ${autoViewName(filters, facets)}` : 'Deal review pack built (all open deals)');
    } catch { toast.error('Could not build the deal review pack'); } finally { setPacking(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid="margin-filter-bar">
      <Filter className="w-3.5 h-3.5 text-[#4ABFCF]" />
      <span className="text-[11px] font-semibold text-[#0F2040] mr-1">Filter deals</span>
      {FILTER_DIMS.map(([k, label]) => <Dim key={k} dimKey={k} label={label} options={facets?.[k] || []} selected={filters[k] || []} onChange={(v) => setFilters((f) => ({ ...f, [k]: v }))} />)}
      {setViews && <SavedViews views={views} setViews={setViews} filters={filters} setFilters={setFilters} facets={facets} />}
      <span className="ml-auto text-[11px] text-slate-500 tabular-nums" data-testid="margin-filter-match">{active ? <><b className="text-[#0F2040]">{matched}</b> of {universe} estimates match</> : <>{universe} estimates</>}</span>
      {active > 0 && <button type="button" onClick={() => setFilters({ ...EMPTY_FILTERS })} className="h-7 inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 text-[11px] text-slate-600 hover:bg-slate-50" data-testid="margin-filter-clear"><X className="w-3 h-3" /> Clear all</button>}
      <button type="button" onClick={downloadPack} disabled={packing} className="h-7 inline-flex items-center gap-1 rounded-md border border-[#0F2040] bg-[#0F2040] px-2 text-[11px] text-white hover:bg-[#1a3260] disabled:opacity-60" title={active ? `Deal review pack for ${autoViewName(filters, facets)}` : 'Deal review pack — all open deals'} data-testid="margin-filter-deal-pack">
        {packing ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />} Deal pack{active ? ' (filtered)' : ''}
      </button>
    </div>
  );
}
